import ArrowButton from 'components/ArrowButton'
import { View } from 'components/View'
import React from 'react'
import { useSwipeable } from 'react-swipeable'
import useSelector from 'modules/component/utils/hooks/useSelector.js'
import isSmallScreenViewportSelect from 'modules/viewport/selectors/is-small-screen'
import { logEventAnalyticsUtil } from 'modules/analytics/utils/log-event/index.js'

const {
  STYLES_FLEX_DIRECTION,
  STYLES_ALIGN_ITEMS
} = require('modules/styles/constants/index.cjs')

export const NEXT = 'NEXT'
export const PREV = 'PREV'

const getInitialState = (numItems) => (
  {
    pos: numItems - 1,
    sliding: false,
    dir: NEXT,
    paginationIndex: 0
  }
)

const getOrder = (index, pos, numItems) => {
  return index - pos < 0
    ? numItems - Math.abs(index - pos)
    : index - pos
}

const AUTOPLAY_TIME_MS = 5000

const Swiper = ({
  children,
  autoplay,
  renderArrows,
  leftArrowPressEvent,
  rightArrowPressEvent,
  delay,
  style
}) => {
  const numItems = React.Children.count(children)
  const [state, dispatch] = React.useReducer(reducer, getInitialState(numItems))
  const isSmallScreen = useSelector(isSmallScreenViewportSelect)
  const intervalIdRef = React.useRef()
  const delayTimeoutIdRef = React.useRef()

  React.useEffect(() => {
    const initAutoPlay = () => {
      intervalIdRef.current = setInterval(() => {
        slide(NEXT)
      }, AUTOPLAY_TIME_MS)
    }

    if (autoplay && delay) {
      delayTimeoutIdRef.current = setTimeout(() => {
        initAutoPlay()
      }, delay)
    } else if (autoplay) {
      initAutoPlay()
    }

    return () => {
      clearInterval(intervalIdRef.current)
      clearTimeout(delayTimeoutIdRef.current)
    }
  }, [])

  const slide = (dir) => {
    dispatch({
      type: dir,
      numItems
    })
    setTimeout(() => {
      dispatch({ type: 'stopSliding' })
    }, 50)
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      swipe(NEXT)
    },
    onSwipedRight: () => swipe(PREV),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true
  })

  const getTransformValue = () => {
    if (!state.sliding) {
      return 'translateX(-100%)'
    }

    if (state.dir === 'PREV') {
      return 'translateX(calc(2 * (-100%)))'
    }

    return 'translateX(0%)'
  }

  const swipe = (type) => {
    if (autoplay) {
      clearInterval(intervalIdRef.current)
      intervalIdRef.current = setInterval(() => slide(NEXT), 5000)
    }
    slide(type)
  }

  return (
    <div {...handlers} style={style}>
      <View
        style={{
          width: '100%',
          overflow: 'hidden'
        }}
      >
        <View jc>
          {renderArrows && (
            <View
              row
              mW='1270px'
              w={isSmallScreen
                ? '95%'
                : '100%'}
              style={{
                justifyContent: 'space-between',
                position: 'absolute',
                left: 0,
                right: 0,
                marginLeft: 'auto',
                marginRight: 'auto',
                zIndex: 1
              }}
            >
              <ArrowButton
                type='left'
                onClick={() => {
                  swipe(PREV)
                  if (leftArrowPressEvent) {
                    logEventAnalyticsUtil(leftArrowPressEvent)
                  }
                }}
              />
              <ArrowButton
                type='right'
                onClick={() => {
                  swipe(NEXT)
                  if (rightArrowPressEvent) {
                    logEventAnalyticsUtil(rightArrowPressEvent)
                  }
                }}
              />
            </View>
          )}
          <View
            style={{
              display: 'flex',
              flexDirection: STYLES_FLEX_DIRECTION.ROW,
              transition: state.sliding
                ? 'none'
                : 'transform 1s ease',
              transform: getTransformValue()
            }}
          >
            {React.Children.map(children, (child, index) => (
              <View
                key={index}
                style={{
                  flex: '1 0 100%',
                  flexBasis: '100%',
                  order: getOrder(index, state.pos, numItems)
                }}
              >
                {child}
              </View>
            ))}
          </View>
        </View>
        <View
          style={{
            flexDirection: STYLES_FLEX_DIRECTION.ROW,
            justifyContent: STYLES_ALIGN_ITEMS.CENTER,
            marginBottom: '1em'
          }}
        >
          {Array(numItems)
            .fill()
            .map((_, itemIndex) => (
              <View
                mt='3em'
                w='5.7px'
                h='5.7px'
                mr='0.431em'
                bgc={state.paginationIndex === itemIndex
                  ? '#292929'
                  : 'white'}
                key={itemIndex}
                style={{
                  border: '0.5px solid #292929',
                  borderRadius: '50%'
                }}
              />
            ))}
        </View>
      </View>
    </div>
  )
}

function reducer (state, action) {
  switch (action.type) {
    case PREV:
      return {
        ...state,
        dir: PREV,
        sliding: true,
        pos: state.pos === 0
          ? action.numItems - 1
          : state.pos - 1,
        paginationIndex: state.paginationIndex === 0
          ? action.numItems - 1
          : state.paginationIndex - 1
      }
    case NEXT:
      return {
        ...state,
        dir: NEXT,
        sliding: true,
        pos: state.pos === action.numItems - 1
          ? 0
          : state.pos + 1,
        paginationIndex: state.paginationIndex === action.numItems - 1
          ? 0
          : state.paginationIndex + 1
      }
    case 'stopSliding':
      return {
        ...state,
        sliding: false
      }
    default:
      return state
  }
}

export default Swiper
